import { Wallet, Coins, ShieldCheck, Rocket, ExternalLink, ShieldAlert } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { BUY_LINKS } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";

export const HowToBuy = () => {
  const { t } = useLanguage();
  const STEPS = [
    {
      testid: "step-1",
      icon: Wallet,
      title: t("htb.s1t"),
      desc: t("htb.s1d"),
    },
    {
      testid: "step-2",
      icon: Coins,
      title: t("htb.s2t"),
      desc: t("htb.s2d"),
    },
    {
      testid: "step-3",
      icon: ShieldCheck,
      title: t("htb.s3t"),
      desc: t("htb.s3d"),
    },
    {
      testid: "step-4",
      icon: Rocket,
      title: t("htb.s4t"),
      desc: t("htb.s4d"),
    },
  ];

  return (
  <section id="how-to-buy" className="scroll-mt-24 px-5 py-24 sm:px-8">
    <div className="mx-auto max-w-7xl">
      <div>
        <Reveal>
          <Eyebrow>{t("htb.eyebrow")}</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-5 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t("htb.t1")} <span className="text-flow">{t("htb.t2")}</span>
          </h2>
        </Reveal>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.testid} delay={0.08 * i}>
            <div
              data-testid={s.testid}
              className="group relative h-full overflow-hidden rounded-3xl border border-purple-500/15 bg-[#0E0E16] p-6 transition-all hover:-translate-y-1 hover:border-[#14F195]/50 sm:p-7"
            >
              <span className="font-display pointer-events-none absolute -right-2 -top-6 text-8xl font-extrabold text-flow opacity-90 transition-opacity group-hover:opacity-100 sm:text-9xl">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#14141E] text-[#14F195] shadow-inner">
                <s.icon size={20} />
              </div>
              <h3 className="font-display mt-5 text-lg font-bold text-[#F5F3FF]">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">
                {s.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div
          data-testid="buy-verify-callout"
          className="mt-6 flex items-start gap-3 rounded-2xl border border-purple-500/30 bg-[#14141E] p-4 sm:items-center sm:p-5"
        >
          <ShieldAlert size={20} className="mt-0.5 shrink-0 text-[#14F195] sm:mt-0" />
          <p className="text-sm leading-relaxed text-gray-300">
            <span className="font-bold text-[#F5F3FF]">{t("htb.safetyT")}</span>{" "}
            {t("htb.safetyB")}
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 rounded-3xl border border-purple-500/15 bg-[#0E0E16]/70 p-6 sm:p-8">
          <span className="font-display mr-2 text-base font-bold text-gray-300 sm:text-lg">
            {t("htb.field")}
          </span>
          {BUY_LINKS.map((b) => (
            <a
              key={b.testid}
              data-testid={`htb-${b.testid}`}
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-[#14141E] px-5 py-2.5 text-sm font-semibold text-purple-100 transition-colors hover:border-[#14F195]/60 hover:text-[#14F195]"
            >
              {b.name}
              <ExternalLink size={13} className="opacity-60" />
            </a>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
  );
};
